import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import styles from './ColorSelect.module.scss';
import messages from './messages';
import AlertBanner from '../common/AlertBanner/AlertBanner';
import { VARIANT_SELECTION_SIZE } from '../../blocks/pdpV2/VariantSelection/constants';

const COLOR_STONE_GREY_KEY = 'stoneGrey';
// These colors have a white oos line because otherwise it's not visible.
const whiteOutOfStockLineColors = [COLOR_STONE_GREY_KEY];

const ColorSelect = ({
  colors,
  selectedColor,
  onChange,
  showColorLabel = true,
  hasDisclaimer = false,
  size = VARIANT_SELECTION_SIZE.MEDIUM,
}) => {
  const { formatMessage } = useIntl();

  const currentlySelectedColor = useMemo(
    () => colors.find((color) => color.key === selectedColor.key),
    [colors, selectedColor],
  );

  return (
    <div className={styles[`color-select-${size}`]}>
      {showColorLabel && (
        <label htmlFor="colors" id="colors-label">
          {formatMessage(messages.color)}
          <span className={styles['selected-variant']}>
            {selectedColor.label}
          </span>
        </label>
      )}
      {currentlySelectedColor?.subheadTextForColor && (
        <div className={styles.subHeadTextColor}>
          <span>{currentlySelectedColor.subheadTextForColor}</span>
        </div>
      )}
      {hasDisclaimer && (
        <AlertBanner
          type="info"
          message={formatMessage(messages.kidsDisclaimer)}
        />
      )}
      <ul
        id="colors"
        className={styles.container}
        role="listbox"
        aria-labelledby="colors-label"
      >
        {colors.map(({ key, label, colorHex, isOutOfStock }) => (
          <li
            key={key}
            className={classnames({
              [styles.active]: key === selectedColor.key,
              [styles.item]: !isOutOfStock,
              [styles.outOfStock]: isOutOfStock,
            })}
            onClick={() => {
              if (!isOutOfStock) {
                onChange({ key, label });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onChange({ key, label });
              }
            }}
            role="option"
            aria-selected={key === selectedColor.key}
            aria-label={label}
            style={{ backgroundColor: colorHex }}
            tabIndex="0"
          >
            {isOutOfStock && (
              <div
                className={classnames(
                  styles[key.replace('-', '_')],
                  whiteOutOfStockLineColors.includes(key)
                    ? styles.outOfStockWhiteLineColor
                    : styles.outOfStockColor,
                )}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

ColorSelect.propTypes = {
  colors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      colorHex: PropTypes.string,
    }).isRequired,
  ).isRequired,
  selectedColor: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  showColorLabel: PropTypes.bool,
  hasDisclaimer: PropTypes.bool,
  size: PropTypes.string,
};

export default ColorSelect;
